import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/config';
import '../styles/Login.css';

const DOMAIN = '@jyothyit.ac.in';

function EyeOpen() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function Login() {
  const [form,     setForm]     = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [remember, setRemember] = useState(false);
  const navigate = useNavigate();

  const [showForgot,    setShowForgot]    = useState(false);
  const [forgotForm,    setForgotForm]    = useState({ username: '', new_password: '', confirm_password: '' });
  const [forgotMsg,     setForgotMsg]     = useState('');
  const [forgotErr,     setForgotErr]     = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPass,   setShowNewPass]   = useState(false);
  const [showConfPass,  setShowConfPass]  = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('rememberedEmail');
    if (saved) {
      setForm(f => ({ ...f, username: saved }));
      setRemember(true);
    }
  }, []);

  const handleLogin = async e => {
    e.preventDefault();
    setError('');
    if (!form.username.endsWith(DOMAIN)) {
      setError(`Please use your institutional email ending with ${DOMAIN}`);
      return;
    }
    setLoading(true);
    try {
      const res = await API.post('/api/auth/login', {
        username: form.username,
        password: form.password,
      });
      if (remember) {
        localStorage.setItem('rememberedEmail', form.username);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user',  JSON.stringify(res.data.user));
      try {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        }
      } catch (_) {}
      if (res.data.user.role === 'admin') {
        navigate('/dashboard');
      } else if (res.data.user.role === 'hod') {
        navigate('/hod-dashboard');
      } else {
        navigate('/verify');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async e => {
    e.preventDefault();
    setForgotErr(''); setForgotMsg('');
    if (!forgotForm.username.endsWith(DOMAIN)) {
      setForgotErr(`Email must end with ${DOMAIN}`); return;
    }
    if (forgotForm.new_password !== forgotForm.confirm_password) {
      setForgotErr('Passwords do not match'); return;
    }
    if (forgotForm.new_password.length < 6) {
      setForgotErr('Password must be at least 6 characters'); return;
    }
    setForgotLoading(true);
    try {
      const res = await API.post('/api/users/reset-password', {
        username    : forgotForm.username,
        new_password: forgotForm.new_password,
      });
      if (res.data.success) {
        setForgotMsg('✅ Password reset successfully! You can now login.');
        setTimeout(() => { closeForgot(); }, 2000);
      }
    } catch (err) {
      setForgotErr(err.response?.data?.message || 'Reset failed. Check your email.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotForm({ username: '', new_password: '', confirm_password: '' });
    setForgotMsg(''); setForgotErr('');
    setShowNewPass(false); setShowConfPass(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">

        <div className="login-header">
          <img src="/images/jit-logo.png" alt="JIT" className="login-logo-img"
            onError={e => { e.target.style.display = 'none'; }} />
          <h1 className="login-college">Jyothy Institute of Technology</h1>
          <p className="login-college-sub">Tataguni, Off Kanakapura Road, Bengaluru - 560082</p>
          <div className="login-badge">JAST — Jyothy Admission Scholarship Test</div>
        </div>

        {error && <div className="error-box">⚠ {error}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-field">
            <label className="login-label">UserID</label>
            <input
              className="form-input"
              type="email"
              value={form.username}
              onChange={e => { setError(''); setForm(f => ({ ...f, username: e.target.value })); }}
              placeholder={`yourname${DOMAIN}`}
              autoComplete="username"
              required
            />
            <p className="field-hint">Must end with {DOMAIN}</p>
          </div>

          <div className="form-field">
            <label className="login-label">Password</label>
            <div className="input-password-wrap">
              <input
                className="form-input"
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => { setError(''); setForm(f => ({ ...f, password: e.target.value })); }}
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
              <button type="button" className="show-pass-btn"
                onClick={() => setShowPass(p => !p)} tabIndex={-1}>
                {showPass ? <EyeClosed /> : <EyeOpen />}
              </button>
            </div>
          </div>

          <div className="login-options-row">
            <label className="remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
              />
              Remember me
            </label>
            <button type="button" className="forgot-btn" onClick={() => setShowForgot(true)}>
              Forgot Password?
            </button>
          </div>

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Logging in…' : 'Login'}
          </button>
        </form>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgot && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h2 className="modal-title">🔒 Reset Password</h2>
              <button className="modal-close-btn" onClick={closeForgot}>✕</button>
            </div>

            {forgotErr && <div className="error-box">⚠ {forgotErr}</div>}
            {forgotMsg && <div className="info-box">{forgotMsg}</div>}

            <form onSubmit={handleForgotReset}>
              <div className="form-field">
                <label className="login-label">UserID</label>
                <input className="form-input" type="email"
                  value={forgotForm.username}
                  onChange={e => setForgotForm(f => ({ ...f, username: e.target.value }))}
                  placeholder={`yourname${DOMAIN}`}
                  required />
                <p className="field-hint">Must end with {DOMAIN}</p>
              </div>

              <div className="form-field">
                <label className="login-label">New Password</label>
                <div className="input-password-wrap">
                  <input className="form-input"
                    type={showNewPass ? 'text' : 'password'}
                    value={forgotForm.new_password}
                    onChange={e => setForgotForm(f => ({ ...f, new_password: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    required />
                  <button type="button" className="show-pass-btn"
                    onClick={() => setShowNewPass(p => !p)} tabIndex={-1}>
                    {showNewPass ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <div className="form-field">
                <label className="login-label">Confirm New Password</label>
                <div className="input-password-wrap">
                  <input className="form-input"
                    type={showConfPass ? 'text' : 'password'}
                    value={forgotForm.confirm_password}
                    onChange={e => setForgotForm(f => ({ ...f, confirm_password: e.target.value }))}
                    placeholder="Re-enter new password"
                    required />
                  <button type="button" className="show-pass-btn"
                    onClick={() => setShowConfPass(p => !p)} tabIndex={-1}>
                    {showConfPass ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForgot}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Resetting…' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}